import { Skeleton, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FeatureFlagRow } from './FeatureFlagRow';
import { useTranslator } from 'translation/useTranslator';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import axios from 'utils/axios';
import { FeatureFlagOrganizationDto } from '../../../../@types/organization';
import { FeatureFlagId } from '../../../../@types/featureFlags';
import { TextField } from '@nps.today/component-library';
import { Text } from 'components/text/Text';

export const AdminFeatureFlags = () => {
  const translator = useTranslator();
  const { flags, loading, updateFeatureFlag } = useFeatureFlags();
  const [organizations, setOrganizations] = useState<FeatureFlagOrganizationDto[]>([]);
  const [search, setSearch] = useState('');

  const getOrganizations = async () => {
    const response = await axios.get('/organizations');
    setOrganizations(response.data);
  };

  useEffect(() => {
    getOrganizations();
  }, []);

  const visibleFlags = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return flags;
    }

    return flags.filter(
      (flag) =>
        FeatureFlagId[flag.id]?.toLowerCase().includes(term) ||
        flag.description?.toLowerCase().includes(term)
    );
  }, [flags, search]);

  return (
    <Stack gap={2}>
      <Stack flexDirection="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Text variant="h6">{translator.featureFlags()}</Text>
        <TextField
          dataTestId="feature-flag-search"
          size="small"
          label={translator.search()}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: '16rem' }}
        />
      </Stack>
      {loading && (
        <Stack gap={2}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} variant="rounded" height={132} />
          ))}
        </Stack>
      )}
      {!loading && visibleFlags.length === 0 && (
        <Text variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 4 }}>
          {translator.noFeatureFlagsFound()}
        </Text>
      )}
      {!loading && visibleFlags.length > 0 && (
        <Stack gap={2}>
          {visibleFlags.map((flag) => (
            <FeatureFlagRow
              key={flag.id}
              featureFlag={flag}
              organizations={organizations}
              updateFeatureFlag={updateFeatureFlag}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
